import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Loading } from '../../components/common/Loading';
import { Input } from '../../components/common/Input';
import { Avatar } from '../../components/common/Avatar';
import { CoachFormCard } from '../../components/widgets/CoachFormCard';
import { useTenantStore } from '../../stores/tenantStore';
import { useDatumStore } from '../../stores/datumStore';
import { ROUTES } from '../../utils/constants';
import {
  listCohorts,
  createCohort,
  getCohort,
  deleteCohort,
  addCohortMember,
  removeCohortMember,
  markCohortVisited,
} from '../../api/cohorts';
import { deleteDatum } from '../../api/datum';
import type { Datum } from '../../api/datum';
import { listUsers } from '../../api/users';
import { getChatMessages, type ChatMessage } from '../../api/messaging';
import type { Cohort, CohortMember } from '../../types/messaging';
import type { User } from '../../types/user';

interface GroupsProps {
  tenant: string;
}

export function Groups({ tenant }: GroupsProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = useTenantStore();
  const { loadDatumByPrincipal, getDatumByPrincipal, principalLoading } = useDatumStore();
  const groupLabel = config?.customization?.dashboardLabel || 'Group';

  // Get initial selected cohort from navigation state
  const initialCohortId = (location.state as { selectedCohortId?: string })?.selectedCohortId;

  const [groups, setGroups] = useState<Cohort[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [creating, setCreating] = useState(false);

  // Selected group and its data
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(initialCohortId || null);
  const [selectedGroup, setSelectedGroup] = useState<Cohort | null>(null);
  const [groupMembers, setGroupMembers] = useState<CohortMember[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingGroupData, setLoadingGroupData] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());

  // Add member state
  const [showAddMember, setShowAddMember] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  // Signal creation state (unused now, kept for compatibility)
  const [isCreatingDatum, setIsCreatingDatum] = useState(false);

  // Collapsible sections state
  const [isMembersCollapsed, setIsMembersCollapsed] = useState(false);
  const [isMessagesCollapsed, setIsMessagesCollapsed] = useState(false);
  const [isPostsCollapsed, setIsPostsCollapsed] = useState(false);

  // Menu state
  const [showMenu, setShowMenu] = useState(false);

  // Get datum from store for selected group
  const datum = selectedGroupId ? getDatumByPrincipal(selectedGroupId) : [];
  const isDatumLoading = selectedGroupId ? principalLoading.get(selectedGroupId) : false;

  useEffect(() => {
    loadGroups();
  }, [tenant]);

  useEffect(() => {
    if (selectedGroupId) {
      loadGroupData(selectedGroupId);
      // Mark cohort as visited when selected
      markCohortVisited(tenant, selectedGroupId).catch(console.error);
    }
  }, [selectedGroupId, tenant]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showMenu && !target.closest('.relative')) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  const loadGroups = async () => {
    try {
      const response = await listCohorts(tenant);
      setGroups(response.cohorts);
      // Auto-select first group if available
      if (response.cohorts.length > 0 && !selectedGroupId) {
        setSelectedGroupId(response.cohorts[0].id);
      }
    } catch (error) {
      console.error('Failed to load groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGroupData = async (groupId: string) => {
    setLoadingGroupData(true);
    try {
      const [cohortData, chatData] = await Promise.all([
        getCohort(tenant, groupId),
        getChatMessages(tenant, groupId).catch(() => ({ target: { id: groupId, name: '', avatarUrl: null, kind: 'cohort' }, messages: [] })),
      ]);

      // Load datum via store
      loadDatumByPrincipal(tenant, groupId);

      setSelectedGroup(cohortData.cohort);
      // Filter out coaches from members list
      const nonCoachMembers = cohortData.members.filter(
        (m) => m.role !== 'coach'
      );
      setGroupMembers(nonCoachMembers);
      setMessages(chatData.messages);
      setSelectedMembers(new Set());
    } catch (error) {
      console.error('Failed to load group data:', error);
    } finally {
      setLoadingGroupData(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;

    setCreating(true);
    try {
      const response = await createCohort(tenant, newGroupName.trim());
      const newGroups = [...groups, response.cohort];
      setGroups(newGroups);
      setSelectedGroupId(response.cohort.id);
      setShowCreateDialog(false);
      setNewGroupName('');
    } catch (error) {
      console.error('Failed to create group:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!selectedGroupId || !selectedGroup) return;
    if (!confirm(`Are you sure you want to delete ${selectedGroup.name}?`)) return;

    try {
      await deleteCohort(tenant, selectedGroupId);
      const newGroups = groups.filter((g) => g.id !== selectedGroupId);
      setGroups(newGroups);
      setSelectedGroupId(newGroups.length > 0 ? newGroups[0].id : null);
      setSelectedGroup(null);
      setGroupMembers([]);
    } catch (error) {
      console.error('Failed to delete group:', error);
    }
  };

  const handleSelectAll = () => {
    if (selectedMembers.size === groupMembers.length) {
      setSelectedMembers(new Set());
    } else {
      setSelectedMembers(new Set(groupMembers.map((m) => m.userId)));
    }
  };

  const handleSelectMember = (userId: string) => {
    const newSelected = new Set(selectedMembers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedMembers(newSelected);
  };

  const handleRemoveMembers = async () => {
    if (!selectedGroupId || selectedMembers.size === 0) return;

    try {
      for (const userId of selectedMembers) {
        await removeCohortMember(tenant, selectedGroupId, userId);
      }
      setSelectedMembers(new Set());
      loadGroupData(selectedGroupId);
    } catch (err) {
      console.error('Failed to remove members:', err);
    }
  };

  const handleOpenAddMember = async () => {
    setShowAddMember(true);
    setLoadingUsers(true);
    try {
      const response = await listUsers(tenant, { limit: 100 });
      const memberIds = new Set(groupMembers.map((m) => m.userId));
      setUsers(response.users.filter((u) => !memberIds.has(u.id)));
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAddMember = async (userId: string) => {
    if (!selectedGroupId) return;

    try {
      await addCohortMember(tenant, selectedGroupId, userId);
      loadGroupData(selectedGroupId);
      setUsers(users.filter((u) => u.id !== userId));
    } catch (error) {
      console.error('Failed to add member:', error);
    }
  };

  const handleCreatePost = () => {
    if (!selectedGroupId) return;
    // Go directly to form editor
    navigate(ROUTES.coach.createForm(tenant, selectedGroupId));
  };

  const handleEditDatum = (signal: Datum) => {
    if (!selectedGroupId) return;

    if (signal.kind === 'reminder' || signal.kind === 'checklist' || signal.kind === 'form' || signal.kind === 'poll' || signal.kind === 'goal' || signal.kind === 'habit') {
      navigate(ROUTES.coach.editForm(tenant, selectedGroupId, signal.id));
      return;
    }
  };

  const handleDeleteDatum = async (signalId: string) => {
    if (!selectedGroupId) return;
    try {
      await deleteDatum(tenant, signalId);
      // Reload datum from store
      loadDatumByPrincipal(tenant, selectedGroupId);
    } catch (err) {
      console.error('Failed to delete signal:', err);
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  // Sort datum by creation date (newest first)
  const sortedDatum = [...datum].sort((a, b) => b.createdAt - a.createdAt);

  if (loading) {
    return <Loading message={`Loading ${groupLabel.toLowerCase()}s...`} />;
  }

  return (
    <div className="space-y-6">
      {/* Header with Dropdown and Action Buttons */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4 flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{groupLabel}s</h1>
          {groups.length > 0 && (
            <select
              value={selectedGroupId || ''}
              onChange={(e) => setSelectedGroupId(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-gray-900 font-medium"
            >
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>

          {showMenu && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
              <button
                onClick={() => {
                  setShowCreateDialog(true);
                  setShowMenu(false);
                }}
                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-gray-700"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Create {groupLabel}</span>
              </button>
              {selectedGroupId && (
                <>
                  <button
                    onClick={() => {
                      handleOpenAddMember();
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                    <span>Add Member</span>
                  </button>
                  <button
                    onClick={() => {
                      handleDeleteGroup();
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-red-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span>Delete {groupLabel}</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      {groups.length === 0 ? (
        <Card>
          <div className="text-center py-12 text-gray-500">
            <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <p className="mt-4 text-lg font-medium">No {groupLabel.toLowerCase()}s yet</p>
            <p className="mt-2 text-sm">Create your first {groupLabel.toLowerCase()} to get started</p>
            <Button variant="primary" className="mt-6" onClick={() => setShowCreateDialog(true)}>
              Create {groupLabel}
            </Button>
          </div>
        </Card>
      ) : loadingGroupData ? (
        <Loading message={`Loading ${groupLabel.toLowerCase()}...`} />
      ) : selectedGroup ? (
        <>
          {/* Selection Actions */}
          {selectedMembers.size > 0 && (
            <Card className="bg-blue-50 border-blue-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-blue-800">
                  {selectedMembers.size} member{selectedMembers.size > 1 ? 's' : ''} selected
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setSelectedMembers(new Set())}>
                    Clear Selection
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    onClick={handleRemoveMembers}
                  >
                    Remove from {groupLabel}
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Messages Section (Collapsible Tile) */}
          <Card>
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setIsMessagesCollapsed(!isMessagesCollapsed)}
            >
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900">Messages</h3>
              </div>
              <svg
                className={`w-5 h-5 text-gray-400 transition-transform ${isMessagesCollapsed ? '' : 'rotate-180'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            {!isMessagesCollapsed && (
              <div className="mt-4 space-y-3">
                {/* Group Chat */}
                <button
                  onClick={() => navigate(`/${tenant}/chat/${selectedGroupId}`)}
                  className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-gray-900">{selectedGroup.name} Chat</p>
                      <p className="text-sm text-gray-500">
                        {messages.length > 0
                          ? `${messages.length} message${messages.length !== 1 ? 's' : ''}`
                          : 'No messages yet'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>

                {/* Individual Member DMs */}
                {groupMembers.map((member) => (
                  <button
                    key={member.userId}
                    onClick={() => navigate(`/${tenant}/chat/${member.userId}`)}
                    className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar src={member.avatarUrl} name={member.name || 'Unknown'} size="sm" />
                      <div className="text-left">
                        <p className="font-medium text-gray-900">{member.name || 'Unknown'}</p>
                        <p className="text-sm text-gray-500">Direct message</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </button>
                ))}

                {groupMembers.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-2">No members to message</p>
                )}
              </div>
            )}
          </Card>

          {/* Members Section */}
          <Card>
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setIsMembersCollapsed(!isMembersCollapsed)}
            >
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900">Members</h3>
                {groupMembers.length > 0 && (
                  <span className="text-sm text-gray-500">
                    ({groupMembers.length})
                  </span>
                )}
              </div>
              <svg
                className={`w-5 h-5 text-gray-400 transition-transform ${isMembersCollapsed ? '' : 'rotate-180'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            {!isMembersCollapsed && (
              <div className="mt-4">
                {groupMembers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <p className="mt-2">No members in this {groupLabel.toLowerCase()} yet</p>
                    <p className="mt-1 text-sm">Use the menu button above to add members</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left">
                            <input
                              type="checkbox"
                              checked={selectedMembers.size === groupMembers.length && groupMembers.length > 0}
                              onChange={handleSelectAll}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            User
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Role
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Joined
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {groupMembers.map((member) => (
                          <tr key={member.userId} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={selectedMembers.has(member.userId)}
                                onChange={() => handleSelectMember(member.userId)}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <Avatar src={member.avatarUrl} name={member.name || 'Unknown'} size="sm" />
                                <div>
                                  <p className="font-medium text-gray-900">{member.name || 'Unknown'}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 capitalize">{member.role}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {new Date(member.joinedAt).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/${tenant}/chat/${member.userId}`)}
                              >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                </svg>
                                Message
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Posts Section (Datum only, no messages) */}
          <Card>
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setIsPostsCollapsed(!isPostsCollapsed)}
            >
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900">Posts</h3>
                {sortedDatum.length > 0 && (
                  <span className="text-sm text-gray-500">
                    ({sortedDatum.length})
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreatePost();
                  }}
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create Post
                </Button>
                <svg
                  className={`w-5 h-5 text-gray-400 transition-transform ${isPostsCollapsed ? '' : 'rotate-180'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {!isPostsCollapsed && (
              <div className="mt-4">
                {isDatumLoading ? (
                  <Loading message="Loading posts..." />
                ) : sortedDatum.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="mt-2">No posts yet</p>
                    <Button variant="outline" size="sm" className="mt-4" onClick={handleCreatePost}>
                      Create your first post
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {sortedDatum.map((signal) => (
                      <CoachFormCard
                        key={signal.id}
                        datum={signal}
                        members={groupMembers}
                        onEdit={() => handleEditDatum(signal)}
                        onDelete={() => handleDeleteDatum(signal.id)}
                        showCohortBadge={false}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        </>
      ) : null}

      {/* Create Group Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Create {groupLabel}</h2>
              <button onClick={() => setShowCreateDialog(false)} className="text-gray-500 hover:text-gray-700">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <Input
                label={`${groupLabel} Name`}
                placeholder={`Enter ${groupLabel.toLowerCase()} name...`}
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-3 p-4 border-t bg-gray-50 rounded-b-lg">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateGroup}
                disabled={!newGroupName.trim()}
                isLoading={creating}
              >
                Create {groupLabel}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Member Dialog */}
      {showAddMember && selectedGroup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Add Member to {selectedGroup.name}</h2>
              <button onClick={() => setShowAddMember(false)} className="text-gray-500 hover:text-gray-700">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 border-b">
              <Input
                placeholder="Search users..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
              />
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {loadingUsers ? (
                <Loading message="Loading users..." />
              ) : filteredUsers.length === 0 ? (
                <p className="text-center text-gray-500 py-4">No users available to add</p>
              ) : (
                <div className="space-y-2">
                  {filteredUsers.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => handleAddMember(user.id)}
                      className="w-full flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Avatar src={user.avatarUrl} name={user.name} size="sm" />
                      <div className="text-left">
                        <p className="font-medium text-gray-900">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                      <svg className="w-5 h-5 text-gray-400 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end p-4 border-t bg-gray-50 rounded-b-lg">
              <Button variant="outline" onClick={() => setShowAddMember(false)}>
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
